<template>
  <ModuleWrapper :moduleId="moduleId" title="Chat Experience" @initializeData="initializeData">
    <template #main>
      <div class="tw-mb-7">
        <p class="tw-mb-2 tw-block tw-text-xs">Chat Settings</p>
        <StudioDetailedSelect
          :options="chatOptions"
          :selectedOption="selectedChatOption"
          @onSelect="handleChatVisiblityChange"
        />
      </div>
      <StudioToggleWithLabel
        id="session-chat-toggle"
        label="Session Chat"
        subLabel="Enable an additional chat thread specific to each session."
        class="tw-mb-7"
        :checked="broadcastChatEnabled"
        @update:checked="handleBroadcastChatToggle"
      />
      <StudioToggleWithLabel
        v-if="organization.useNewChatApp"
        id="thread-chat-toggle"
        label="Threaded Chat"
        subLabel="Enable threaded conversations. Attendees can reply to specific messages and create conversation threads. This affects both Public and Session chat."
        class="tw-mb-7"
        :checked="threadChatEnabled"
        @update:checked="handleThreadChatToggle"
      />
      <StudioToggleWithLabel
        id="p2p-chat-toggle"
        label="Direct Message"
        subLabel="Attendees can privately message other attendees, speakers, and organizers."
        class="tw-mb-7"
        :checked="p2pChatEnabled"
        @update:checked="handleP2PChatToggle"
      />
      <StudioToggleWithLabel
        v-if="false"
        id="text-qna-toggle"
        label="Text Q&A"
        subLabel="Attendees can ask questions and upvote other attendees' questions."
        class="tw-mb-7"
        :checked="hasTextQna"
        @update:checked="handleTextQnaToggle"
      />
      <StudioToggleWithLabel
        v-if="hasTextQna"
        id="text-qna-moderated-toggle"
        label="Moderate Text Q&A"
        subLabel="Require moderators to approve or reject questions before they are shown to the audience in the Q&A tab."
        class="tw-mb-7"
        :checked="textQnaModerated"
        @update:checked="handleTextQnaModeratedToggle"
      />
      <StudioToggleWithLabel
        v-if="hasTextQna && organization.assignQnaToSpeaker"
        id="assign-qna-to-speaker-toggle"
        label="Assign Questions from Q&A"
        subLabel="Allow attendee questions in Q&A to be assigned to a specific speaker or staff."
        class="tw-mb-7"
        :checked="assignQnaToSpeaker"
        @update:checked="handleAssignQnaToSpeakerToggle"
      />
      <StudioToggleWithLabel
        v-if="hasTextQna"
        id="anonymous-qna-toggle"
        label="Anonymous Text Q&A"
        subLabel="Attendees can ask questions anonymously."
        class="tw-mb-7"
        :checked="anonymousQnaEnabled"
        @update:checked="handleAnonymousQnaToggle"
      />
      <div>
        <StudioToggleWithLabel
          v-if="hasTextQna"
          id="show-answerers-identity-toggle"
          label="Show Answerers' Identity"
          subLabel="Show the name of the person who answered the question."
          class="tw-mb-4"
          :checked="showAnswerersIdentity"
          @update:checked="handleShowAnswerersIdentityToggle"
        />
        <StudioInput
          v-if="hasTextQna && showAnswerersIdentity"
          id="textqna-answerers-alias-input"
          label="Answerers' Alias"
          class="tw-mb-7"
          :modelValue="textqnaAnswerersAlias"
          @update:modelValue="value => editTextqnaAnswerersAlias(value)"
        />
      </div>
      <StudioToggleWithLabel
        v-if="false"
        id="video-qna-toggle"
        label="Video Q&A"
        subLabel="Attendees can ask questions and upvote other attendees' questions."
        class="tw-mb-7"
        :checked="hasVideoQna"
        @update:checked="handleVideoQnaToggle"
      />
      <StudioToggleWithLabel
        id="gifs-toggle"
        label="GIFs"
        subLabel="Attendees can send GIFs in chat."
        class="tw-mb-7"
        :checked="gifsEnabled"
        @update:checked="handleGifsEnabledToggle"
      />
      <StudioToggleWithLabel
        v-if="organization.useFloatingEmoji"
        id="floating-emoji-reactions-toggle"
        label="Floating Emoji Reactions"
        subLabel="Attendees can engage by tapping emojis that float over the stage, giving speakers instant real-time feedback."
        class="tw-mb-7"
        :checked="floatingEmojiReactionsEnabled"
        @update:checked="handleFloatingEmojiReactionsToggle"
      />
      <StudioToggleWithLabel
        id="discussions-chat-toggle"
        label="Room Chat"
        subLabel="Attendees can chat with other attendees in Rooms."
        class="tw-mb-7"
        :checked="discussionsChatEnabled"
        @update:checked="handleDiscussionsChatToggle"
      />
      <StudioToggleWithLabel
        v-if="isBoothsTabEnabled"
        id="booth-chat-toggle"
        label="Booth Chat"
        subLabel="Attendees can chat with other attendees in Booths."
        class="tw-mb-7"
        :checked="boothChatEnabled"
        @update:checked="handleBoothChatToggle"
      />
    </template>
  </ModuleWrapper>
</template>

<script lang="ts">
import { ChatVisibiltyEnum } from '@goldcast/api/backend';
import { defineComponent } from 'vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';

import { CurrentOrganization } from '@/store/modules/org';
import { StudioInput, StudioToggleWithLabel } from '@/studioComponents';
import StudioDetailedSelect from '@/studioComponents/StudioDetailedSelect.vue';

export default defineComponent({
  name: 'ChatExperience',
  components: {
    ModuleWrapper,
    StudioToggleWithLabel,
    StudioInput,
    StudioDetailedSelect
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      broadcastChatEnabled: false,
      p2pChatEnabled: false,
      threadChatEnabled: false,
      textQnaModerated: false,
      assignQnaToSpeaker: false,
      anonymousQnaEnabled: false,
      showAnswerersIdentity: false,
      textqnaAnswerersAlias: '',
      gifsEnabled: false,
      floatingEmojiReactionsEnabled: false,
      discussionsChatEnabled: false,
      boothChatEnabled: false,
      hasTextQna: false,
      chatVisibilty: null as ChatVisibiltyEnum | null,
      hasVideoQna: false,
      selectedChatOption: null as { value: ChatVisibiltyEnum; label: string; description: string } | null,
      chatOptions: [
        {
          value: 'PUBLIC' as ChatVisibiltyEnum,
          label: 'On for all',
          description:
            'All attendees, speakers, and organizers can post messages for everyone to see live during an event.'
        },
        {
          value: 'PRIVATE' as ChatVisibiltyEnum,
          label: 'Announcements only',
          description: 'Only organizers and speakers can post announcements for attendees to view. '
        },
        {
          value: 'OFF' as ChatVisibiltyEnum,
          label: 'Off',
          description: 'Public chat tab will be hidden from view for all attendees, speakers, and organizers. '
        }
      ]
    };
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    }),
    ...mapGetters('orgPricingConfig', {
      isBoothsTabEnabled: 'getIsBoothsTabEnabled'
    }),
    ...(mapGetters('org', { organization: 'getCurrentOrg' }) as {
      organization: () => CurrentOrganization;
    }),
    ...mapGetters('studioEventList', {
      currentEvent: 'getCurrentEvent'
    })
  },
  methods: {
    ...mapActions('branding', ['onSelectModule']),
    ...mapMutations('branding', {
      setChangesMadeInEvent: 'setChangesMadeInEvent'
    }),
    initializeData() {
      const moduleKeys = [
        'broadcast_chat_disabled',
        'p2p_chat_disabled',
        'chat_visibilty',
        'thread_chat_disabled',
        'is_text_qna_moderated',
        'anonymous_qna_enabled',
        'show_answerers_identity',
        'textqna_answerers_alias',
        'gifs_enabled',
        'floating_emoji_reactions_enabled',
        'discussions_chat_disabled',
        'booth_chat_disabled',
        'have_text_qna',
        'have_video_qna',
        'assign_speaker_qna'
      ];

      const {
        broadcast_chat_disabled,
        p2p_chat_disabled,
        thread_chat_disabled,
        is_text_qna_moderated,
        chat_visibilty,
        anonymous_qna_enabled,
        show_answerers_identity,
        textqna_answerers_alias,
        gifs_enabled,
        floating_emoji_reactions_enabled,
        discussions_chat_disabled,
        booth_chat_disabled,
        have_text_qna,
        have_video_qna,
        assign_speaker_qna
      } = this.getInitialModuleData(moduleKeys);

      this.updateSelectedChatOption(chat_visibilty);
      this.broadcastChatEnabled = !broadcast_chat_disabled;
      this.p2pChatEnabled = !p2p_chat_disabled;
      this.threadChatEnabled = !thread_chat_disabled;
      this.textQnaModerated = is_text_qna_moderated;
      this.assignQnaToSpeaker = assign_speaker_qna;
      this.anonymousQnaEnabled = anonymous_qna_enabled;
      this.showAnswerersIdentity = show_answerers_identity;
      this.textqnaAnswerersAlias = textqna_answerers_alias || '';
      this.gifsEnabled = gifs_enabled;
      this.floatingEmojiReactionsEnabled = floating_emoji_reactions_enabled;
      this.discussionsChatEnabled = !discussions_chat_disabled;
      this.boothChatEnabled = !booth_chat_disabled;
      this.hasTextQna = have_text_qna;
      this.hasVideoQna = have_video_qna;
    },
    updateSelectedChatOption(chatVisiblity: string) {
      this.selectedChatOption = this.chatOptions.find(option => option.value === chatVisiblity) || this.chatOptions[0];
    },
    handleBroadcastChatToggle(e) {
      this.broadcastChatEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        broadcast_chat_disabled: !this.broadcastChatEnabled
      });
    },
    handleChatVisiblityChange(option: { value: ChatVisibiltyEnum; label: string; description: string }) {
      this.chatVisibilty = option.value;
      this.setChangesMadeInEvent({
        chat_visibilty: option.value
      });
      this.updateSelectedChatOption(option.value);
    },
    handleP2PChatToggle(e) {
      this.p2pChatEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        p2p_chat_disabled: !this.p2pChatEnabled
      });
    },
    handleThreadChatToggle(e) {
      this.threadChatEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        thread_chat_disabled: !this.threadChatEnabled
      });
    },
    handleTextQnaToggle(e) {
      this.hasTextQna = e.target.checked;
      this.setChangesMadeInEvent({
        have_text_qna: this.hasTextQna
      });
    },
    handleVideoQnaToggle(e) {
      this.hasVideoQna = e.target.checked;
      this.setChangesMadeInEvent({
        have_video_qna: this.hasVideoQna
      });
    },
    handleTextQnaModeratedToggle(e) {
      this.textQnaModerated = e.target.checked;
      this.setChangesMadeInEvent({
        is_text_qna_moderated: this.textQnaModerated
      });
    },
    handleAssignQnaToSpeakerToggle(e) {
      this.assignQnaToSpeaker = e.target.checked;
      this.setChangesMadeInEvent({
        assign_speaker_qna: this.assignQnaToSpeaker
      });
    },
    handleAnonymousQnaToggle(e) {
      this.anonymousQnaEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        anonymous_qna_enabled: this.anonymousQnaEnabled
      });
    },
    handleShowAnswerersIdentityToggle(e) {
      this.showAnswerersIdentity = e.target.checked;
      this.setChangesMadeInEvent({
        show_answerers_identity: this.showAnswerersIdentity
      });
    },
    editTextqnaAnswerersAlias(text) {
      this.textqnaAnswerersAlias = text;
      this.setChangesMadeInEvent({
        textqna_answerers_alias: this.textqnaAnswerersAlias
      });
    },
    handleGifsEnabledToggle(e) {
      this.gifsEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        gifs_enabled: this.gifsEnabled
      });
    },
    handleFloatingEmojiReactionsToggle(e) {
      this.floatingEmojiReactionsEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        floating_emoji_reactions_enabled: this.floatingEmojiReactionsEnabled
      });
    },
    handleDiscussionsChatToggle(e) {
      this.discussionsChatEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        discussions_chat_disabled: !this.discussionsChatEnabled
      });
    },
    handleBoothChatToggle(e) {
      this.boothChatEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        booth_chat_disabled: !this.boothChatEnabled
      });
    }
  }
});
</script>
